import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { Observable, filter, fromEventPattern, map, share, tap } from 'rxjs';

export const SANITIZE_HASH_REGEX = /^[^#]*/;

@Injectable({
  providedIn: 'root'
})
export class HashTriggerService {
  public readonly hashChange$ = this.createHashChange$();

  private readonly triggerHashes = [];
  private readonly location = inject(Location);

  private isResetting = false;
  private urlChangeCount = 0;

  registerHash(...hashes: string[]): void {
    hashes.filter(hash => !this.triggerHashes.includes(hash)).forEach(hash => this.triggerHashes.push(hash));
  }

  private createHashChange$(): Observable<string> {
    return fromEventPattern(handler => this.location.onUrlChange(handler)).pipe(
      map(([url]) => this.checkHash(url)),
      filter<string>(Boolean),
      tap(_ => this.resetHash()),
      share()
    );
  }

  /**
   * Check if current url is containing a hash value defined for triggering an action.
   * Also verify there's a hash at all and also if the location change has not been
   * triggered by a hash reset (see `resetHash()`)
   * Finding registered hashes is using RegExp, as registered hashes can also be regex-like.
   * @param url
   * @private
   */
  private checkHash(url: string): string {
    this.urlChangeCount++;
    if (!this.isResetting) {
      const hash = url.replace(SANITIZE_HASH_REGEX, '');
      return hash ? this.triggerHashes.some(key => hash.match(`^${key}$`)) && hash : null;
    }
    return '';
  }

  /**
   * Remove added hash fragment from location url.
   * This requires to first replace the actual history entry with url without hash,
   * then navigate back in order to not pollute history with unwanted entries.
   * In order to prevent above urlChange listener capturing the location manipulation,
   * `isResetting` flag is set to true and reverted after a small delay.
   * @private
   */
  private resetHash(): void {
    this.isResetting = true;
    const changeCount = this.urlChangeCount;
    this.location.replaceState(this.location.path());
    // invoke back only if page hasn't been loaded with a hash,
    // as then a history.back() could mistakenly load previous page.
    if (changeCount > 1) {
      this.location.back();
    }

    setTimeout(() => (this.isResetting = false), 100);
  }
}
